import { useEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { ExclamationTriangleIcon, Squares2X2Icon, PlusIcon, ArrowUturnLeftIcon, MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import type { ConfigurationSectionDefinition } from '@/app/types/aql-configuration';
import { ConfigurationDetailSheet, type ConfigurationFieldDefinition } from './configuration-detail-sheet';
import { useTranslation } from '@/app/hooks/useTranslation';

interface ConfigurationMapConfig<T extends Record<string, any>> {
  idKey: string;
  latitudeKey: string;
  longitudeKey: string;
  title: (item: T) => string;
  subtitle?: (item: T) => string;
}

interface ConfigurationSectionPageProps<T extends Record<string, any>> {
  section: ConfigurationSectionDefinition;
  items: T[];
  columns: Array<{ key: string; label: string; render?: (item: T) => string | number | JSX.Element }>;
  fields: ConfigurationFieldDefinition[];
  searchKeys: string[];
  createLabel: string;
  onSave: (item: T, mode: 'create' | 'edit') => Promise<void>;
  onDelete: (item: T) => Promise<void>;
  createDraft: () => T;
  metadata?: string;
  notice?: string;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  columnPreferenceKey?: string;
  mapConfig?: ConfigurationMapConfig<T>;
  layout?: 'table' | 'grid';
  toolbarActions?: JSX.Element;
  itemActions?: (item: T) => JSX.Element;
  renderCardFooter?: (item: T) => JSX.Element | null;
  renderEditorExtra?: (draft: Record<string, any>, setDraft: Dispatch<SetStateAction<Record<string, any>>>) => ReactNode;
  destructiveDelete?: {
    getName: (item: T) => string;
    title: string;
    description: (item: T) => string;
    confirmationLabel?: string;
    confirmButtonLabel?: string;
  };
}

const statusBadgeClass = (value: string) =>
  value === 'active' || value === 'online' || value === 'true'
    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
    : value === 'warning' || value === 'degraded'
      ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300'
      : 'border-border bg-muted/55 text-muted-foreground dark:bg-muted/40';

const readColumnPreferences = (key: string, columnKeys: string[]) => {
  if (typeof window === 'undefined') return columnKeys;

  try {
    const saved = window.localStorage.getItem(key);
    if (!saved) return columnKeys;

    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return columnKeys;

    const validKeys = parsed.filter((item): item is string => typeof item === 'string' && columnKeys.includes(item));
    return validKeys.length ? validKeys : columnKeys;
  } catch {
    return columnKeys;
  }
};

const markerIcon = L.divIcon({
  className: '',
  html: '<span class="block h-4 w-4 rounded-full border-2 border-white bg-sky-400 shadow-[0_0_0_4px_rgba(14,165,233,0.25),0_12px_24px_-10px_rgba(0,0,0,0.8)]"></span>',
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

const selectedMarkerIcon = L.divIcon({
  className: '',
  html: '<span class="block h-5 w-5 rounded-full border-2 border-white bg-emerald-400 shadow-[0_0_0_5px_rgba(16,185,129,0.28),0_14px_28px_-10px_rgba(0,0,0,0.85)]"></span>',
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});

function ConfigurationMapPanel<T extends Record<string, any>>({
  items,
  mapConfig,
  selectedId,
  onSelect
}: {
  items: T[];
  mapConfig: ConfigurationMapConfig<T>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const mapNodeRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!mapNodeRef.current || mapRef.current) return;

    const map = L.map(mapNodeRef.current, {
      attributionControl: false,
      zoomControl: true
    }).setView([39.6, -8], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19
    }).addTo(map);
    L.control.attribution({ prefix: false }).addAttribution('&copy; OpenStreetMap').addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      markersRef.current = null;
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const markerLayer = markersRef.current;
    if (!map || !markerLayer) return;

    markerLayer.clearLayers();
    const bounds = L.latLngBounds([]);
    let selectedPosition: L.LatLngExpression | null = null;

    items.forEach((item, index) => {
      const latitude = Number(item[mapConfig.latitudeKey]);
      const longitude = Number(item[mapConfig.longitudeKey]);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      const id = String(item[mapConfig.idKey] ?? index);
      const position: L.LatLngExpression = [latitude, longitude];
      const marker = L.marker(position, { icon: id === selectedId ? selectedMarkerIcon : markerIcon });
      const popupContent = document.createElement('div');
      const title = document.createElement('div');
      title.className = 'font-semibold';
      title.textContent = mapConfig.title(item);
      popupContent.appendChild(title);

      const subtitle = mapConfig.subtitle?.(item);
      if (subtitle) {
        const detail = document.createElement('div');
        detail.className = 'text-xs';
        detail.textContent = subtitle;
        popupContent.appendChild(detail);
      }

      marker.bindPopup(popupContent);
      marker.on('click', () => onSelect(id));
      marker.addTo(markerLayer);
      bounds.extend(position);
      if (id === selectedId) selectedPosition = position;
    });

    if (selectedPosition) {
      map.setView(selectedPosition, Math.max(map.getZoom(), 13), { animate: true });
    } else if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [34, 34], maxZoom: 12 });
    } else {
      map.setView([39.6, -8], 6);
    }
  }, [items, mapConfig, onSelect, selectedId]);

  return (
    <div className="min-h-[430px] overflow-hidden rounded-2xl border border-border bg-muted/20">
      <div ref={mapNodeRef} className="h-[430px] w-full" />
    </div>
  );
}

export function ConfigurationSectionPage<T extends Record<string, any>>({
  section,
  items,
  columns,
  fields,
  searchKeys,
  createLabel,
  onSave,
  onDelete,
  createDraft,
  metadata,
  notice,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  searchPlaceholder,
  emptyTitle,
  emptyDescription,
  columnPreferenceKey,
  mapConfig,
  layout = 'table',
  toolbarActions,
  itemActions,
  renderCardFooter,
  renderEditorExtra,
  destructiveDelete
}: ConfigurationSectionPageProps<T>) {
  const { t: translate } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => translate('configuration', key, params);
  const columnSignature = columns.map((column) => column.key).join('|');
  const columnKeys = useMemo(() => columns.map((column) => column.key), [columnSignature]);
  const resolvedColumnPreferenceKey = columnPreferenceKey ?? `aql-config-columns:local:${section.id}`;
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<T | null>(null);
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null);
  const [mode, setMode] = useState<'create' | 'edit'>('edit');
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() =>
    readColumnPreferences(resolvedColumnPreferenceKey, columnKeys)
  );

  useEffect(() => {
    setVisibleColumnKeys(readColumnPreferences(resolvedColumnPreferenceKey, columnKeys));
  }, [columnKeys, resolvedColumnPreferenceKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const normalizedKeys = visibleColumnKeys.filter((key) => columnKeys.includes(key));
    window.localStorage.setItem(
      resolvedColumnPreferenceKey,
      JSON.stringify(normalizedKeys.length ? normalizedKeys : columnKeys)
    );
  }, [columnKeys, resolvedColumnPreferenceKey, visibleColumnKeys]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const lower = query.toLowerCase();
    return items.filter((item) =>
      searchKeys.some((key) => String(item[key] ?? '').toLowerCase().includes(lower))
    );
  }, [items, query, searchKeys]);

  useEffect(() => {
    if (!mapConfig) return;
    if (filtered.some((item, index) => String(item[mapConfig.idKey] ?? index) === selectedMapId)) return;
    const firstWithCoordinates = filtered.find((item) =>
      Number.isFinite(Number(item[mapConfig.latitudeKey])) &&
      Number.isFinite(Number(item[mapConfig.longitudeKey]))
    );
    setSelectedMapId(firstWithCoordinates ? String(firstWithCoordinates[mapConfig.idKey] ?? 0) : null);
  }, [filtered, mapConfig, selectedMapId]);

  const visibleColumnKeySet = useMemo(() => new Set(visibleColumnKeys), [visibleColumnKeys]);
  const visibleColumns = useMemo(
    () => columns.filter((column) => visibleColumnKeySet.has(column.key)),
    [columns, visibleColumnKeySet]
  );

  const toggleColumn = (key: string, checked: boolean) => {
    setVisibleColumnKeys((current) => {
      const currentKeys = current.filter((item) => columnKeys.includes(item));
      const nextKeys = new Set(currentKeys);

      if (checked) {
        nextKeys.add(key);
      } else {
        if (nextKeys.size <= 1) return currentKeys;
        nextKeys.delete(key);
      }

      const orderedKeys = columnKeys.filter((item) => nextKeys.has(item));
      return orderedKeys.length ? orderedKeys : currentKeys;
    });
  };

  const openDeleteConfirmation = (item: T) => {
    setDeleteTarget(item);
    setDeleteConfirmation('');
  };

  const closeDeleteConfirmation = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
    setDeleteConfirmation('');
  };

  const expectedDeleteName = deleteTarget && destructiveDelete
    ? destructiveDelete.getName(deleteTarget)
    : '';
  const canConfirmDelete = destructiveDelete
    ? Boolean(expectedDeleteName) && deleteConfirmation.trim() === expectedDeleteName
    : Boolean(deleteTarget);

  const confirmDelete = async () => {
    if (!deleteTarget || !canConfirmDelete) return;
    try {
      setIsDeleting(true);
      await onDelete(deleteTarget);
      setDeleteTarget(null);
      setDeleteConfirmation('');
    } catch (error: any) {
      toast.error(error?.message ?? tr('deleteRecordFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="aql-density-tight space-y-3">
      <style>
        {`
          @keyframes aqlSectionCardIn {
            0% {
              opacity: 0;
              transform: translate3d(0, 16px, 0) scale(0.99);
              filter: blur(10px);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
              filter: blur(0);
            }
          }

          @keyframes aqlSectionSearchGlow {
            0%, 100% { box-shadow: 0 0 0 rgba(56, 189, 248, 0); }
            50% { box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.18), 0 12px 28px -22px rgba(56, 189, 248, 0.55); }
          }

          @keyframes aqlRowIn {
            0% {
              opacity: 0;
              transform: translate3d(0, 10px, 0);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0);
            }
          }

          .aql-config-section-card {
            animation: aqlSectionCardIn 620ms cubic-bezier(0.22, 1, 0.36, 1) both;
          }

          .aql-config-toolbar-input {
            transition: border-color 180ms ease, background-color 180ms ease, transform 180ms ease, box-shadow 180ms ease;
          }

          .aql-config-toolbar-input:focus {
            animation: aqlSectionSearchGlow 2.8s ease-in-out infinite;
            transform: translateY(-1px);
          }

          .aql-config-table-row {
            animation: aqlRowIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
            transition: background-color 180ms ease, transform 180ms ease;
          }

          .aql-config-table-row:hover {
            transform: translateY(-1px);
            background: linear-gradient(90deg, rgba(56, 189, 248, 0.05), rgba(15, 23, 42, 0.02));
          }

          @media (prefers-reduced-motion: reduce) {
            .aql-config-section-card,
            .aql-config-table-row,
            .aql-config-toolbar-input:focus {
              animation: none !important;
            }

            .aql-config-table-row:hover,
            .aql-config-toolbar-input:focus {
              transform: none !important;
            }
          }
        `}
      </style>

      <section className="aql-adaptive-card aql-config-header aql-config-section-card rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{tr('configuration')}</p>
            <h2 className="mt-1 text-lg font-semibold text-foreground">{section.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{section.subtitle}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {metadata ? <Badge variant="outline" className="border-border bg-muted/35 text-muted-foreground">{metadata}</Badge> : null}
            {canCreate ? (
              <Button
                onClick={() => {
                  setMode('create');
                  setSelected(createDraft());
                  setOpen(true);
                }}
                className="inline-flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                {createLabel}
              </Button>
            ) : null}
          </div>
        </div>
        {notice ? (
          <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            {notice}
          </div>
        ) : null}
      </section>

      <section className="aql-adaptive-card aql-config-header aql-config-section-card rounded-2xl border border-border bg-card shadow-sm" style={{ animationDelay: '90ms' }}>
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder ?? tr('searchPlaceholder', { section: section.label.toLowerCase() })}
              className="aql-config-toolbar-input w-full rounded-xl border border-border bg-muted/30 py-2.5 pl-10 pr-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm text-muted-foreground">{tr('records', { count: filtered.length })}</div>
            {toolbarActions}
            {!mapConfig ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="inline-flex h-10 items-center gap-2">
                  <Squares2X2Icon className="h-4 w-4" />
                  {tr('fields')}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 p-2">
                <p className="px-2 py-1.5 text-sm font-medium text-foreground">{tr('visibleFields')}</p>
                <div className="max-h-72 overflow-y-auto py-1">
                  {columns.map((column) => {
                    const checked = visibleColumnKeySet.has(column.key);
                    const isLastVisible = checked && visibleColumnKeys.length <= 1;

                    return (
                      <label
                        key={column.key}
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                          isLastVisible
                            ? 'cursor-not-allowed text-muted-foreground/60'
                            : 'cursor-pointer text-popover-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isLastVisible}
                          onChange={(event) => toggleColumn(column.key, event.target.checked)}
                          className="h-4 w-4 rounded border-border accent-primary"
                        />
                        <span>{column.label}</span>
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setVisibleColumnKeys(columnKeys)}
                  className="mt-1 flex w-full items-center gap-2 border-t border-border px-2 py-2 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ArrowUturnLeftIcon className="h-4 w-4" />
                  {tr('showAll')}
                </button>
              </PopoverContent>
            </Popover>
            ) : null}
          </div>
        </div>
      </section>

      <div className="aql-config-section-card" style={{ animationDelay: '120ms' }}>
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/20 py-12 text-center">
            <p className="text-lg font-medium text-foreground">{emptyTitle ?? tr('noData')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{emptyDescription ?? tr('createFirstRecord')}</p>
          </div>
        ) : layout === 'grid' && !mapConfig ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item, index) => {
              const itemId = String(item.id ?? item.user_id ?? item.slug ?? index);
              const statusColumn = columns.find((column) => column.key === 'status' || column.key === 'is_active');
              const statusRawValue = statusColumn
                ? statusColumn.render
                  ? statusColumn.render(item)
                  : item[statusColumn.key]
                : null;
              const normalizedStatus = typeof statusRawValue === 'string'
                ? statusRawValue.toLowerCase()
                : typeof item.is_active === 'boolean'
                  ? item.is_active
                    ? 'active'
                    : 'inactive'
                  : '';
              const statusLabel = normalizedStatus ? tr(normalizedStatus) : tr('toConfirm');
              const fieldsToShow = columns
                .slice(1)
                .filter((column) => column.key !== statusColumn?.key)
                .slice(0, 4);
              const titleValue = columns[0]?.render ? columns[0].render(item) : item[columns[0]?.key];
              const descriptionValue = typeof item.description === 'string' && item.description.trim()
                ? item.description.trim()
                : null;

              return (
                <div
                    key={itemId}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setMode('edit');
                      setSelected(item);
                      setOpen(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setMode('edit');
                      setSelected(item);
                      setOpen(true);
                    }}
                    className="aql-config-table-row group flex min-h-[260px] cursor-pointer flex-col rounded-lg border border-border bg-card p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/35 hover:bg-muted/20 hover:shadow-md"
                    style={{ animationDelay: `${120 + index * 32}ms` } as CSSProperties}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                          {columns[0]?.label ?? 'Registo'}
                        </p>
                        {typeof titleValue === 'string' || typeof titleValue === 'number' ? (
                          <h3 className="mt-2 line-clamp-2 text-lg font-semibold leading-6 text-foreground">{titleValue}</h3>
                        ) : (
                          <div className="mt-2 min-w-0 text-foreground">{titleValue}</div>
                        )}
                        {descriptionValue ? (
                          <p className="mt-2 line-clamp-2 text-sm leading-5 text-muted-foreground">{descriptionValue}</p>
                        ) : null}
                      </div>
                      {normalizedStatus ? (
                        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${statusBadgeClass(normalizedStatus)}`}>
                          {statusLabel}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4 space-y-3">
                      {fieldsToShow.map((column) => {
                        const rawValue = column.render ? column.render(item) : item[column.key];
                        const shouldBadge = typeof rawValue === 'string' && ['active', 'inactive', 'warning', 'online', 'offline', 'degraded'].includes(rawValue.toLowerCase());
                        const displayValue = shouldBadge ? tr(String(rawValue).toLowerCase()) : rawValue;

                        return (
                          <div key={column.key} className="grid grid-cols-[108px_minmax(0,1fr)] gap-3 border-t border-border/55 pt-3 first:border-t-0 first:pt-0">
                            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{column.label}</span>
                            <span className="min-w-0 text-sm font-medium text-foreground">
                              {shouldBadge ? (
                                <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(String(rawValue).toLowerCase())}`}>
                                  {displayValue}
                                </span>
                              ) : displayValue === 'A confirmar' || !displayValue ? (
                                <span className="text-muted-foreground">--</span>
                              ) : (
                                displayValue as any
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {renderCardFooter ? renderCardFooter(item) : null}

                    <div className="mt-auto flex items-center justify-end gap-2 border-t border-border/60 pt-4">
                      {itemActions ? <div onClick={(e) => e.stopPropagation()}>{itemActions(item)}</div> : null}
                      {canEdit ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMode('edit');
                            setSelected(item);
                            setOpen(true);
                          }}
                        >
                          {tr('edit')}
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 border-red-300 bg-red-50 px-2 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20 dark:hover:text-red-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteConfirmation(item);
                          }}
                          aria-label="Apagar registo"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                </div>
              );
            })}
          </div>
        ) : mapConfig ? (
          <div className="grid gap-3 lg:grid-cols-[minmax(240px,30%)_1fr]">
            <div className="rounded-2xl border border-border bg-muted/15">
              <div className="max-h-[430px] overflow-y-auto p-2">
                {filtered.map((item, index) => {
                  const itemId = String(item[mapConfig.idKey] ?? index);
                  const selectedOnMap = itemId === selectedMapId;
                  const latitude = Number(item[mapConfig.latitudeKey]);
                  const longitude = Number(item[mapConfig.longitudeKey]);
                  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);

                  return (
                    <div
                      key={itemId}
                      className={`mb-2 rounded-xl border p-3 transition-colors ${
                        selectedOnMap
                          ? 'border-primary/45 bg-primary/10'
                          : 'border-border bg-card shadow-sm hover:border-primary/25 hover:bg-card dark:bg-card/70 dark:hover:bg-card/90'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedMapId(itemId)}
                        className="block w-full text-left"
                      >
                        <p className="truncate text-sm font-semibold text-foreground">{mapConfig.title(item)}</p>
                        {mapConfig.subtitle ? (
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{mapConfig.subtitle(item)}</p>
                        ) : null}
                        <p className="mt-2 text-xs text-muted-foreground">
                          {hasCoordinates ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` : tr('toConfirm')}
                        </p>
                      </button>
                      <div className="mt-3 flex gap-2">
                        {canEdit ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 flex-1"
                            onClick={() => {
                              setMode('edit');
                              setSelected(item);
                              setOpen(true);
                            }}
                          >
                            {tr('edit')}
                          </Button>
                        ) : null}
                        {canDelete ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-red-300 bg-red-50 px-2 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20 dark:hover:text-red-200"
                            onClick={() => openDeleteConfirmation(item)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <ConfigurationMapPanel
              items={filtered}
              mapConfig={mapConfig}
              selectedId={selectedMapId}
              onSelect={setSelectedMapId}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="aql-adaptive-table aql-config-table w-auto min-w-[760px]">
              <TableHeader>
                <TableRow className="border-border">
                  {visibleColumns.map((column) => (
                    <TableHead key={column.key} className="text-muted-foreground">{column.label}</TableHead>
                  ))}
                  <TableHead className="w-[164px] min-w-[164px] px-3 text-right text-muted-foreground">
                    {tr('actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item, index) => (
                  <TableRow
                    key={String(item.id ?? item.user_id ?? item.slug ?? index)}
                    className="aql-config-table-row border-border/70"
                    style={{ animationDelay: `${120 + index * 32}ms` } as CSSProperties}
                  >
                    {visibleColumns.map((column) => {
                      const rawValue = column.render ? column.render(item) : item[column.key];
                      const shouldBadge = typeof rawValue === 'string' && ['active', 'inactive', 'warning', 'online', 'offline', 'degraded'].includes(rawValue.toLowerCase());

                      return (
                        <TableCell key={column.key} className="max-w-[320px] text-foreground">
                          {shouldBadge ? (
                            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs ${statusBadgeClass(String(rawValue).toLowerCase())}`}>
                              {tr(String(rawValue).toLowerCase())}
                            </span>
                          ) : rawValue as any}
                        </TableCell>
                      );
                    })}
                    <TableCell className="w-[164px] min-w-[164px] px-3 text-right">
                      <div className="inline-flex justify-end gap-2">
                        {canEdit ? (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setMode('edit');
                              setSelected(item);
                              setOpen(true);
                            }}
                          >
                            {tr('edit')}
                          </Button>
                        ) : (
                          <span className="px-2 text-xs text-muted-foreground">{tr('externallyManaged')}</span>
                        )}
                        {canDelete ? (
                          <Button
                            variant="outline"
                            className="border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20 dark:hover:text-red-200"
                            onClick={() => openDeleteConfirmation(item)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ConfigurationDetailSheet
        open={open}
        onOpenChange={setOpen}
        mode={mode}
        title={section.title}
        description={section.subtitle}
        fields={fields}
        value={selected}
        renderAfterFields={renderEditorExtra}
        onSave={(next) => onSave(next, mode)}
      />

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(next) => {
        if (!next) closeDeleteConfirmation();
      }}>
        <DialogContent className="border-red-500/35 bg-card p-0 shadow-2xl sm:max-w-xl">
          <DialogHeader className="border-b border-border px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl border border-red-300 bg-red-50 p-2 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                <ExclamationTriangleIcon className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg text-foreground">
                  {destructiveDelete?.title ?? 'Apagar registo'}
                </DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-6 text-muted-foreground">
                  {deleteTarget && destructiveDelete
                    ? destructiveDelete.description(deleteTarget)
                    : 'Confirma que queres apagar este registo. Esta ação remove o item desta secção.'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-200">
              Esta ação é permanente.
            </div>
            {destructiveDelete ? (
              <>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-foreground">
                    {destructiveDelete.confirmationLabel ?? 'Escreve o nome exato para confirmar'}
                  </span>
                  <input
                    value={deleteConfirmation}
                    onChange={(event) => setDeleteConfirmation(event.target.value)}
                    placeholder={expectedDeleteName}
                    disabled={isDeleting}
                    className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-red-400"
                  />
                </label>
                <p className="text-xs text-muted-foreground">
                  Nome esperado: <span className="font-semibold text-foreground">{expectedDeleteName}</span>
                </p>
              </>
            ) : null}
          </div>
          <DialogFooter className="border-t border-border px-5 py-4">
            <Button type="button" variant="outline" onClick={closeDeleteConfirmation} disabled={isDeleting}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={!canConfirmDelete || isDeleting}
              className="bg-red-500 text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {isDeleting ? 'A apagar...' : destructiveDelete?.confirmButtonLabel ?? 'Apagar registo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
